import React from 'react';
import TechBadge from '../../common/TechBadge';

// Renders the per-service item rows: Preferred Products (material) and
// Work Steps (work). Shared between ServiceCard (shop tech) and the expanded
// StopCard (mobile tech) so the row treatment stays identical across both
// surfaces.
//
// Props:
//   items           — array of request_service_tasks (material + work)
//   currentUserId   — to determine ownership (mine / peer / unassigned)
//   serviceState    — 'new' | 'booked' | 'started' | 'finished'; work-item
//                     checkboxes are interactive only when 'started'
//   totalTaskCount  — used so the "Unassigned" caption only appears when at
//                     least one task is assigned to someone (avoids labeling
//                     every item "Unassigned" before techs are wired up)
//   onToggleItem    — fired when an interactive work-item checkbox is toggled

const taskOwnership = (item, currentUserId) => {
  const ownerId = item.assigned_technician_id;
  if (ownerId == null) return 'unassigned';
  if (currentUserId && String(ownerId) === String(currentUserId)) return 'mine';
  return 'peer';
};

const ServiceItemsList = ({
  items = [],
  currentUserId,
  serviceState,
  totalTaskCount = 0,
  onToggleItem,
}) => {
  if (!items || items.length === 0) return null;

  const materialItems = items.filter(i => !i.type || i.type === 'material');
  const workItems = items.filter(i => i.type === 'work');

  return (
    <>
      {materialItems.length > 0 && (
        <div className="mb-3">
          <strong>Preferred Products:</strong>
          {materialItems.map(item => {
            const owner = taskOwnership(item, currentUserId);
            const greyed = owner !== 'mine';
            return (
              <div
                key={item.id}
                className="ml-2 mt-1 text-muted"
                style={greyed ? { opacity: 0.55 } : undefined}
              >
                <i className="fa-sharp fa-regular fa-tag mr-2"></i>
                <span>
                  {item.label}
                  {item.recommended_product_name && (
                    <span className="ml-1">
                      — {item.recommended_product_brand ? `${item.recommended_product_brand} - ` : ''}{item.recommended_product_name}
                    </span>
                  )}
                </span>
                {owner === 'peer' && (
                  <span className="ml-2">
                    <TechBadge
                      name={item.assigned_technician_name}
                      color={item.assigned_technician_user_color}
                    />
                  </span>
                )}
                {owner === 'unassigned' && totalTaskCount > 0 && (
                  <span className="ml-2 small">Unassigned</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {workItems.length > 0 && (
        <div className="mb-3">
          <strong>Work Steps:</strong>
          {workItems.map(item => {
            const owner = taskOwnership(item, currentUserId);
            const isMine = owner === 'mine' || !currentUserId;
            const greyed = !isMine;
            const interactive = isMine && serviceState === 'started';
            return (
              <div
                key={item.id}
                className="d-flex align-items-center ml-2 mt-2"
                style={greyed ? { opacity: 0.55 } : undefined}
              >
                <input
                  type="checkbox"
                  checked={item.status === 'completed'}
                  onChange={(e) => {
                    e.stopPropagation();
                    if (interactive && onToggleItem) {
                      onToggleItem(item);
                    }
                  }}
                  disabled={!interactive}
                  className="mr-2"
                  style={{ width: '18px', height: '18px', cursor: interactive ? 'pointer' : 'default' }}
                />
                <label
                  className="mb-0"
                  style={{ cursor: interactive ? 'pointer' : 'default' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (interactive && onToggleItem) {
                      onToggleItem(item);
                    }
                  }}
                >
                  {item.label}
                </label>
                {owner === 'peer' && (
                  <span className="ml-2">
                    <TechBadge
                      name={item.assigned_technician_name}
                      color={item.assigned_technician_user_color}
                    />
                  </span>
                )}
                {owner === 'unassigned' && totalTaskCount > 0 && (
                  <span className="ml-2 small text-muted">Unassigned</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ServiceItemsList;
